import {
    cardLinkNeutralDarkest,
    gradientTextStyles,
} from '@/src/card-linking/components/colors'
import { Field, Form, Formik } from 'formik'
import * as yup from 'yup'
import valid from 'card-validator'
import { CardLinkTextInputWithLabel } from '@/src/card-linking/components/CardLinkTextInputWithLabel'
import { CardLinkFormErrorMessage } from '@/src/card-linking/components/CardLinkFormErrorMessage'
import { useState } from 'react'
import { styled } from '@/src/stitches.config'
import usePostEnrollCard from '@/src/card-linking/hooks/usePostEnrollCard'
import ErrorPopup from '@/src/card-linking/components/ErrorPopup'
import creditCardType, { types as CardType } from 'credit-card-type'
import { TCardLinkFunctions } from '@/src/card-linking/types'
import {
    defaultBodyText,
    defaultDivWrapper,
    defaultHeader,
    standardPrimaryButton,
    standardPrimaryButtonOnMouseEnter,
    standardPrimaryButtonOnMouseLeave,
} from '@/src/card-linking/components/iframeStyles'
import CardLinkLoader from '@/src/card-linking/components/CardLinkLoader'

const FormRowWrapper = styled('div', {
    display: 'flex',
    alignItems: 'left',
    gap: '.3em',
    marginTop: '1em',
    flexDirection: 'column',
    maxWidth: '500px',
    order: 1,
})

interface CardLinkEnrollNewCardFormProps {
    cardLinkSessionId: string
    handleSuccess: Function
    functions: TCardLinkFunctions
    isError: boolean
    adjustTextForNonActivate: boolean
}

export default function CardLinkEnrollNewCardForm({
    cardLinkSessionId,
    handleSuccess,
    functions,
    isError,
    adjustTextForNonActivate,
}: CardLinkEnrollNewCardFormProps) {
    const {
        mutate: postEnrollCard,
        isLoading: isLoadingPostEnrollCard,
        isSuccess: isSuccessPostEnrollCard,
    } = usePostEnrollCard()
    const [showError, setShowError] = useState<boolean>(false)
    const enrollText: string = adjustTextForNonActivate
        ? 'Enroll'
        : 'Enroll and activate'
    const enrollButtonText: string = adjustTextForNonActivate
        ? 'Enroll'
        : 'Enroll and Activate'

    if (isLoadingPostEnrollCard || isSuccessPostEnrollCard) {
        return <CardLinkLoader />
    }

    return (
        <div style={defaultDivWrapper}>
            <h2 style={defaultHeader}>
                <span style={gradientTextStyles}>{enrollText}</span> your debit
                or credit card
            </h2>
            <p style={{ ...defaultBodyText }}>
                Pay with the card you already use instead of remembering to use
                a gift card. Revolutionary, we know!
            </p>
            <h3 style={{ fontSize: '18px', color: cardLinkNeutralDarkest }}>
                Card Information
            </h3>
            <Formik
                initialValues={{
                    cardNumber: '',
                    cardExpiration: '',
                    nameOnCard: '',
                }}
                validationSchema={yup.object({
                    cardNumber: yup
                        .string()
                        .required('Card number required')
                        .test(
                            'test-number',
                            'Card number is invalid',
                            (value) => {
                                return valid.number(value).isValid
                            }
                        )
                        .test(
                            'test-card-network',
                            'Only Visa, Mastercard, or Amex are accepted',
                            (value) => {
                                return creditCardType(
                                    value ? value.replace(/\D/g, '') : ''
                                ).some(function (card) {
                                    return (
                                        card.type === CardType.MASTERCARD ||
                                        card.type === CardType.VISA ||
                                        card.type === CardType.AMERICAN_EXPRESS
                                    )
                                })
                            }
                        ),
                    cardExpiration: yup
                        .string()
                        .required('Expiration required')
                        .test(
                            'test-expiration',
                            'Expiration is invalid or expired. Use format MM/YY.',
                            (value) => {
                                return valid.expirationDate(value).isValid
                            }
                        ),
                    nameOnCard: yup
                        .string()
                        .required('Name on Card is required'),
                })}
                onSubmit={async (values) => {
                    setShowError(false)
                    const cardType = creditCardType(
                        values.cardNumber.replace(/\D/g, '')
                    )
                    if (!cardType[0]) {
                        console.log('Could not determine card type', cardType)
                        setShowError(true)
                        return
                    }
                    const cardTypeString = cardType[0].type

                    const cardExpiration: string =
                        values.cardExpiration.split('/')[0] +
                        `/20${values.cardExpiration.split('/')[1]}`

                    postEnrollCard(
                        {
                            name: values.nameOnCard,
                            type:
                                cardTypeString === 'american-express'
                                    ? 'amex'
                                    : cardTypeString.toLowerCase(),
                            card_number: values.cardNumber,
                            card_expiration: cardExpiration,
                            card_link_session_id: cardLinkSessionId,
                        },
                        {
                            onSuccess: (data) => {
                                handleSuccess(data)
                                if (functions?.setIsActivating) {
                                    functions.setIsActivating(true)
                                }
                            },
                            onError: () => {
                                setShowError(true)
                            },
                        }
                    )
                }}
            >
                {({ isSubmitting, errors, touched }) => (
                    <Form
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            rowGap: 10,
                        }}
                        data-testid={'card-linking-form'}
                        method={'POST'}
                    >
                        <FormRowWrapper>
                            <Field name="cardNumber">
                                {({ field, meta }: any) => (
                                    <>
                                        <CardLinkTextInputWithLabel
                                            {...field}
                                            {...meta}
                                            numFormat={'####-####-####-####'}
                                            label={'Card number'}
                                            autoComplete={'off'}
                                            data-testid={'cardNumber'}
                                            className={'fs-mask fs-exclude'}
                                        />
                                    </>
                                )}
                            </Field>
                            {!!errors.cardNumber && touched.cardNumber && (
                                <CardLinkFormErrorMessage
                                    testId={'errpan'}
                                    message={errors.cardNumber}
                                />
                            )}
                        </FormRowWrapper>
                        <FormRowWrapper>
                            <Field name="cardExpiration">
                                {({ field, meta }: any) => (
                                    <CardLinkTextInputWithLabel
                                        {...field}
                                        {...meta}
                                        numFormat={'##/##'}
                                        label={'Expiration (MM/YY)'}
                                        autoComplete={'off'}
                                        data-testid={'cardExpiration'}
                                        className={'fs-mask fs-exclude'}
                                    />
                                )}
                            </Field>
                            {!!errors.cardExpiration &&
                                touched.cardExpiration && (
                                    <CardLinkFormErrorMessage
                                        testId={'errexpiry'}
                                        message={errors.cardExpiration}
                                    />
                                )}
                        </FormRowWrapper>
                        <FormRowWrapper>
                            <Field name="nameOnCard">
                                {({ field, meta }: any) => (
                                    <CardLinkTextInputWithLabel
                                        {...field}
                                        {...meta}
                                        label={'Name on Card'}
                                        autoComplete={'off'}
                                        data-testid={'nameOnCard'}
                                        className={'fs-mask fs-exclude'}
                                    />
                                )}
                            </Field>
                            {!!errors.nameOnCard && touched.nameOnCard && (
                                <CardLinkFormErrorMessage
                                    testId={'errnameoncard'}
                                    message={errors.nameOnCard}
                                />
                            )}
                        </FormRowWrapper>
                        {(showError || isError) && <ErrorPopup />}
                        <button
                            type="submit"
                            style={standardPrimaryButton}
                            onMouseEnter={(e) => {
                                standardPrimaryButtonOnMouseEnter(e)
                            }}
                            onMouseLeave={(e) => {
                                standardPrimaryButtonOnMouseLeave(e)
                            }}
                        >
                            {isSubmitting ? 'Saving' : enrollButtonText}
                        </button>
                        <p style={{ fontSize: '.83rem' }}>
                            By submitting my payment card information, I
                            authorize each payment card network such as Visa,
                            Mastercard and American Express to monitor
                            transactions made with such card to determine when I
                            have made a qualifying transactions and share such
                            transaction details with PerfectGift so that it may
                            credit my card. I authorize PerfectGift to share my
                            transaction data as provided in the{' '}
                            <a
                                href={
                                    'https://www.perfectgift.com/terms-of-service'
                                }
                            >
                                Terms of Service
                            </a>{' '}
                            and{' '}
                            <a
                                href={
                                    'https://www.perfectgift.com/privacy-policy'
                                }
                            >
                                Privacy Policy
                            </a>
                            . You may opt-out of transaction monitoring of
                            payment cards you entered, at any time, by visiting
                            the PerfectGift website or contacting us at{' '}
                            <a href="mailto:support@perfectgift.com">
                                support@perfectgift.com
                            </a>
                        </p>
                    </Form>
                )}
            </Formik>
        </div>
    )
}
